<template>
  <dialog
    class="PositionModalView"
    data-theme="light"
    data-testid="positionPrompt"
    open
  >
    <div
      class="PositionModalView__backdrop"
      @click="$emit('close')"
    />
    <form
      class="PositionModalView__window"
      :aria-busy="isSaving"
      @submit.prevent="handleSubmit"
    >
      <p class="PositionModalView__prompt">
        Before updating your status, pick your position, so Ball.app can suggest matchups.
      </p>
      <fieldset
        style="display: grid; gap: 12px;"
        :disabled="isSaving"
      >
        <PositionDropdown
          v-model="form.position"
        />
        <FormButton type="submit">
          <span v-if="isSaving">
            Saving…
          </span>
          <span v-else>
            Save
          </span>
        </FormButton>
      </fieldset>
      <p class="PositionModalView__caption">
        You can change your position at any time in your profile.
      </p>
    </form>
  </dialog>
</template>

<script lang="ts" setup>
import FormButton from "@/components/FormButton.vue";
import PositionDropdown from "@/components/PositionDropdown.vue";
import { supabaseClient } from "@/lib/database/supabase";
import useAuthStore from "@/stores/useAuthStore";
import { storeToRefs } from "pinia";
import { ref } from "vue";

const emit = defineEmits<{
  close: [],
}>();

const {
  currentPlayer,
} = storeToRefs(useAuthStore());

const form = ref({
  position: currentPlayer.value?.position || null,
});

const isSaving = ref(false);

async function handleSubmit () {
  if (!currentPlayer.value) return;

  isSaving.value = true;
  const {
    data: updatedPlayer,
    error,
  } = await supabaseClient
    .from("players")
    .update({
      position: form.value.position,
    })
    .eq("id", currentPlayer.value.id)
    .select()
    .maybeSingle();
  isSaving.value = false;

  if (updatedPlayer) {
    currentPlayer.value.position = updatedPlayer.position;
    emit("close");
  }

  if (error) alert(error.message);
}
</script>

<style lang="scss" scoped>
.PositionModalView {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  isolation: isolate;

  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;

  transition: none 350ms;
}

.PositionModalView__backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;

  background-color: var(--themeColorBackdrop);
  backdrop-filter: blur(6px);

  cursor: pointer;

  transition: opacity 350ms var(--easeOutBack);

  .v-leave-active & {
    transition: 350ms;
    transition-timing-function: initial;
  }

  .v-enter-from &,
  .v-leave-to & {
    opacity: 0;
  }
}

.PositionModalView__window {
  display: grid;
  gap: 24px;
  padding: 24px 20px;

  border-radius: 16px;
  background-color: var(--themeColorNeutral0);

  .v-enter-active & {
    transition: 350ms var(--easeOutBack);
    transition-property: opacity, transform;
  }

  .v-leave-active & {
    transition: 350ms;
    transition-property: opacity;
  }

  .v-enter-from & {
    transform: scale(0.8);
    opacity: 0;
  }

  .v-leave-to & {
    opacity: 0;
  }
}

.PositionModalView__prompt {
  font-size: 18px;
  font-stretch: var(--fontWidthExtended);
  line-height: 1.5;
}

.PositionModalView__caption {
  font-size: 16px;
  font-stretch: var(--fontWidthExtended);
  line-height: 1.5;

  font-variation-settings: "ital" 1;
}
</style>
